import { MutableRefObject, useEffect, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { useAppNavigation } from 'contexts/navigation';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { isDocumentationPath } from 'setup/navigation/paths';
import { useDocumentationStore } from '../../store';
import { getEffectiveGalleryDirectory } from '../../slices/gallery/model';
import { areDirectoryIdsEqual } from 'shared/domain/directory/directoryIds';
import { getDisplayedVersionId, selectGalleryUrlState } from './model';
import { isNavigatingOutside, signalNavigationOutsideDocumentation } from './navigationGuard';

/**
 * Keeps the URL in line with the gallery: the open preview shows its file in
 * the URL, closing it returns to the directory the preview was opened from.
 */
export function useGalleryUrlSynchronization(
  isDocumentationDialogOpen: boolean,
  fileToOpenRef: MutableRefObject<string | undefined>
): void {
  const navigateToDirectory = useDocumentationStore.use.navigateToDirectory();
  const {
    goToDocumentationFile,
    goToDocumentationDirectory,
    goToDocumentation,
  } = useAppNavigation();

  const projectId = useAppUrlParams(UrlParamName.PROJECT_ID);
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;

  const isDocumentationDialogOpenRef = useRef(isDocumentationDialogOpen);
  isDocumentationDialogOpenRef.current = isDocumentationDialogOpen;

  useEffect(() => {
    // The URL reflects whether the preview is open and which document it shows,
    // so react only to those - not to raw slide index changes. A slide change
    // while closed (e.g. right-click in search results) or a re-index of the
    // same document must not navigate; removing the shown file (archive) must.
    return useDocumentationStore.subscribe(selectGalleryUrlState, () => {
      const state = useDocumentationStore.getState();

      // If navigation outside documentation is in progress, skip redirect.
      // This flag is set by components before calling external navigation.
      if (isNavigatingOutside()) {
        return;
      }

      // If user navigated away from documentation section, don't redirect back.
      // Use window.location directly because React state/refs may not be updated yet.
      if (!isDocumentationPath(window.location.pathname)) {
        return;
      }

      // If documentation edit dialog is open, skip URL sync to prevent NavigationPrompt
      // from blocking the change and showing "changes not saved" dialog.
      if (isDocumentationDialogOpenRef.current) {
        return;
      }

      const { galleryOpen: open, currentSlide: slide, files: currentFiles } = state;
      const versionId = getDisplayedVersionId(state);

      if (open && slide !== -1) {
        const currentFile = currentFiles[slide];
        if (currentFile?.documentationId) {
          goToDocumentationFile(
            projectIdRef.current,
            currentFile.documentationId,
            { replace: true, versionId }
          );
          return;
        }
        return;
      }

      if (!open) {
        // Reached only when the preview has just closed (see the guard above).
        // Prefer the directory captured when the gallery opened (or the open
        // file's parent). After edit/save, currentDirectory can briefly be root
        // even though the preview was opened from a nested folder.
        const sourceDirectoryId = getEffectiveGalleryDirectory(state);

        if (!areDirectoryIdsEqual(sourceDirectoryId, state.currentDirectory)) {
          // Tell the URL<->store sync effect to stand down while we correct
          // the directory here, so it doesn't bounce us back to root/stale
          // pathname mid-correction.
          signalNavigationOutsideDocumentation();
          void navigateToDirectory(sourceDirectoryId);
        }

        if (sourceDirectoryId) {
          goToDocumentationDirectory(
            projectIdRef.current,
            sourceDirectoryId,
            { replace: true }
          );
        } else if (!fileToOpenRef.current) {
          goToDocumentation(projectIdRef.current, {
            replace: true,
          });
        }
      }
    }, { equalityFn: shallow });
  }, [
    goToDocumentationFile,
    goToDocumentationDirectory,
    goToDocumentation,
    navigateToDirectory,
  ]);
}
